import { motion } from "framer-motion";
import { Handshake, TrendingUp, ShieldAlert, MessageCircleWarning } from "lucide-react";

const useCases = [
  {
    icon: Handshake,
    title: "Negotiation Training",
    subtitle: "Compensation, contracts, partnerships",
    description: "Practice salary negotiations, vendor contracts, and partnership discussions. Learn to anchor, counter-offer, and create win-win outcomes.",
    skills: ["Anchoring", "BATNA analysis", "Active listening", "Win-win framing"],
    example: '"I appreciate the offer, but based on market data and my Q3 results, I believe $145K better reflects my contribution."',
  },
  {
    icon: TrendingUp,
    title: "Sales Conversations",
    subtitle: "Discovery, demos, closing",
    description: "Sharpen your discovery questions, handle price objections, and practice closing techniques with realistic buyer personas.",
    skills: ["Discovery questions", "Value selling", "Price justification", "Closing techniques"],
    example: '"What would it mean for your team if you could reduce onboarding time by 40%? Let me show you exactly how we do that."',
  },
  {
    icon: ShieldAlert,
    title: "Handling Objections",
    subtitle: "Budget, timing, competition",
    description: "Face the toughest objections head-on. Learn to reframe, provide evidence, and turn skeptics into champions.",
    skills: ["Objection reframing", "Social proof", "Risk reversal", "Urgency creation"],
    example: '"I understand budget is tight. Let me walk you through the ROI our similar-sized clients saw in just 90 days."',
  },
  {
    icon: MessageCircleWarning,
    title: "Difficult Workplace Conversations",
    subtitle: "Feedback, conflict, performance reviews",
    description: "Navigate emotionally charged conversations with empathy and clarity. Give feedback that drives change without damaging relationships.",
    skills: ["SBI framework", "Empathetic confrontation", "De-escalation", "Action planning"],
    example: '"I wanted to share some observations about the client presentation. Your analysis was strong — I think we can make the delivery even more impactful."',
  },
];

export default function UseCasesSection() {
  return (
    <section id="use-cases" data-testid="use-cases-section" className="py-[45px] md:py-[90px] stripe-bg-white relative">
      <div className="absolute inset-0 section-glow-left" />
      <div className="max-w-7xl mx-auto px-6 md:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ ease: [0.22, 1, 0.36, 1] }}
          className="max-w-xl mb-16"
        >
          <div className="section-overline mb-4">Use Cases</div>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-navy mb-6">
            Train for the conversations{" "}
            <br className="md:hidden" />
            <span className="text-stripe-text-muted">that matter most</span>
          </h2>
        </motion.div>

        <div className="space-y-6">
          {useCases.map((uc, i) => {
            const isEven = i % 2 === 1;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                className="stripe-card rounded-2xl overflow-hidden hover:border-stripe-primary/15"
              >
                <div className={`grid grid-cols-1 lg:grid-cols-5 gap-0`}>
                  <div className={`lg:col-span-3 p-8 md:p-10 ${isEven ? "lg:order-2" : ""}`}>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-xl bg-stripe-primary/10 flex items-center justify-center group-hover:scale-105 transition-transform">
                        <uc.icon className="w-5 h-5 text-stripe-primary" />
                      </div>
                      <div>
                        <h3 className="font-heading text-lg font-semibold text-navy">{uc.title}</h3>
                        <p className="text-xs text-stripe-text-muted">{uc.subtitle}</p>
                      </div>
                    </div>
                    <p className="text-sm text-stripe-text-secondary leading-relaxed mb-5">{uc.description}</p>
                    <div className="flex flex-wrap gap-2">
                      {uc.skills.map((skill, j) => (
                        <span key={j} className="px-3 py-1 rounded-full text-xs font-medium bg-stripe-primary/8 text-stripe-primary border border-stripe-primary/10">
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className={`lg:col-span-2 bg-slate-50 p-8 md:p-10 flex items-center ${isEven ? "lg:order-1" : ""}`}>
                    <div className="bg-white rounded-xl border border-stripe-border p-5 w-full shadow-sm">
                      <div className="text-xs text-stripe-text-muted mb-2 font-medium">Example Response</div>
                      <p className="text-sm text-navy italic leading-relaxed">{uc.example}</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
